/**
 * Framework Mapper API Client
 * 
 * Node-centric architecture compliance:
 * - Uses /api/templates/framework-mappings CRUD endpoints
 * - Never mutates template JSON
 * - Proper mapping IDs with audit trail
 * 
 * Backend uses snake_case for all fields
 */

import {
  FrameworkMappingResponse,
  CreateFrameworkMappingRequest,
  UpdateFrameworkMappingRequest,
  Template,
  TemplateNode,
  TemplateField,
  FrameworkRequirement,
  FrameworkVersion,
  ApiResponse
} from './frameworkMapper.dto';

/**
 * API Base Configuration
 */
const API_BASE = '/api/compliance/definition';

/**
 * Get authentication headers
 */
function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No authentication token found');
  }
  const csrfToken = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'X-CSRF-Token': csrfToken,
  };
}

/**
 * Handle API response errors
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || error.error || `API Error: ${response.status}`);
  }
  return response.json();
}

/**
 * Build query string from params
 */
function buildQueryString(params: Record<string, any>): string {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, String(value));
    }
  });
  const queryString = queryParams.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Framework Mapper API Client
 * 
 * Uses proper relational mapping endpoints:
 * - GET /api/templates
 * - GET /api/templates/:templateId/nodes
 * - GET /api/templates/nodes/:nodeId/fields
 * - GET /api/templates/framework-requirements
 * - GET /api/templates/:templateId/framework-mappings
 * - GET /api/templates/nodes/:templateNodeId/framework-mappings
 * - POST /api/templates/framework-mappings
 * - PUT /api/templates/framework-mappings/:id
 * - DELETE /api/templates/framework-mappings/:id
 */
export const frameworkMapperApi = {
  // ==========================================================================
  // TEMPLATE STRUCTURE ENDPOINTS
  // ==========================================================================

  /**
   * Get all templates
   * GET /api/templates
   * For framework mapping, only published templates should be returned
   */
  async getTemplates(): Promise<Template[]> {
    const queryString = buildQueryString({ status: 'published' });
    const response = await fetch(`${API_BASE}/templates${queryString}`, {
      headers: getAuthHeaders(),
    });

    const result: ApiResponse<Template[]> = await handleResponse(response);
    return result.data;
  },

  /**
   * Get template nodes for a template
   * GET /api/templates/:templateId/nodes
   */
  async getTemplateNodes(templateId: number): Promise<TemplateNode[]> {
    const response = await fetch(`${API_BASE}/templates/${templateId}/nodes`, {
      headers: getAuthHeaders(),
    });

    const result: ApiResponse<TemplateNode[]> = await handleResponse(response);
    return result.data;
  },

  /**
   * Get all template nodes (for lookup purposes)
   * This is a convenience method that gets nodes for all templates
   */
  async getAllTemplateNodes(): Promise<TemplateNode[]> {
    const templates = await this.getTemplates();
    const allNodes: TemplateNode[] = [];

    for (const template of templates) {
      const nodes = await this.getTemplateNodes(template.id);
      allNodes.push(...nodes);
    }

    return allNodes;
  },

  /**
   * Get fields for a template node
   * GET /api/templates/nodes/:nodeId/fields
   */
  async getNodeFields(nodeId: number): Promise<TemplateField[]> {
    const response = await fetch(`${API_BASE}/templates/nodes/${nodeId}/fields`, {
      headers: getAuthHeaders(),
    });

    const result: ApiResponse<TemplateField[]> = await handleResponse(response);
    return result.data;
  },

  // ==========================================================================
  // FRAMEWORK ENDPOINTS
  // ==========================================================================

  /**
   * Get all frameworks
   * Note: This requires admin role
   * Falls back to framework-requirements endpoint if admin endpoint fails
   */
  async getFrameworks(): Promise<import('./frameworkMapper.adapter').FrameworkUI[]> {
    try {
      const response = await fetch('/api/compliance/definition/frameworks', {
        headers: getAuthHeaders(),
      });

      const result: ApiResponse<any[]> = await handleResponse(response);
      return result.data;
    } catch (error) {
      // If admin endpoint fails, try framework-requirements endpoint
      console.warn('Admin frameworks endpoint failed, trying framework-requirements');
      const requirements = await this.getFrameworkRequirements();
      // Extract unique frameworks from requirements
      const frameworksMap = new Map<number, import('./frameworkMapper.adapter').FrameworkUI>();
      requirements.forEach(requirement => {
        if (!frameworksMap.has(requirement.framework_id)) {
          const framework: import('./frameworkMapper.adapter').FrameworkUI = {
            id: requirement.framework_id,
            name: requirement.framework_name || `Framework ${requirement.framework_id}`
          };
          if (requirement.framework_code !== undefined) {
            framework.code = requirement.framework_code;
          }
          frameworksMap.set(requirement.framework_id, framework);
        }
      });
      return Array.from(frameworksMap.values());
    }
  },

  /**
   * Get framework versions available for mapping
   * GET /api/templates/framework-versions?frameworkId=123
   */
  async getFrameworkVersions(frameworkId?: number): Promise<FrameworkVersion[]> {
    const queryString = frameworkId ? buildQueryString({ frameworkId }) : '';
    const response = await fetch(`${API_BASE}/framework-versions${queryString}`, {
      headers: getAuthHeaders(),
    });

    const result: ApiResponse<FrameworkVersion[]> = await handleResponse(response);
    return result.data;
  },

  /**
   * Get framework requirements available for mapping (legacy method)
   * GET /api/templates/framework-versions?frameworkId=123
   * @deprecated Use getFrameworkVersions instead
   */
  async getFrameworkRequirements(frameworkId?: number): Promise<FrameworkRequirement[]> {
    const versions = await this.getFrameworkVersions(frameworkId);
    // Convert versions to requirements format for backward compatibility
    return versions.map(version => ({
      id: version.id,
      framework_id: version.framework_id,
      code: version.version_number,
      title: version.version_name,
      framework_name: version.framework_name,
      framework_code: version.framework_code
    }));
  },

  // ==========================================================================
  // FRAMEWORK MAPPING ENDPOINTS (CORE)
  // ==========================================================================

  /**
   * Get framework mappings for a template
   * GET /api/templates/:templateId/framework-mappings
   */
  async getTemplateMappings(templateId: number): Promise<FrameworkMappingResponse[]> {
    const response = await fetch(`${API_BASE}/templates/${templateId}/framework-mappings`, {
      headers: getAuthHeaders(),
    });

    const result: ApiResponse<FrameworkMappingResponse[]> = await handleResponse(response);
    return result.data;
  },

  /**
   * Get framework mappings for a template node
   * GET /api/templates/nodes/:templateNodeId/framework-mappings
   */
  async getNodeMappings(templateNodeId: number): Promise<FrameworkMappingResponse[]> {
    const response = await fetch(`${API_BASE}/templates/nodes/${templateNodeId}/framework-mappings`, {
      headers: getAuthHeaders(),
    });

    const result: ApiResponse<FrameworkMappingResponse[]> = await handleResponse(response);
    return result.data;
  },

  /**
   * Get framework mappings for a specific field
   * GET /api/templates/:templateId/fields/:fieldId/framework-mappings
   * Note: Backend currently returns all template mappings for this endpoint
   */
  async getFieldMappings(templateId: number, fieldId: number): Promise<FrameworkMappingResponse[]> {
    const response = await fetch(`${API_BASE}/templates/${templateId}/fields/${fieldId}/framework-mappings`, {
      headers: getAuthHeaders(),
    });

    const result: ApiResponse<FrameworkMappingResponse[]> = await handleResponse(response);
    return result.data;
  },

  /**
   * Create a new framework mapping
   * POST /api/templates/framework-mappings
   *
   * Backend expects: framework_id, framework_version_id, template_node_id, mapping_type
   */
  async createMapping(data: CreateFrameworkMappingRequest): Promise<FrameworkMappingResponse> {
    // Ensure undefined values are converted to null for SQL compatibility
    const cleanData = {
      framework_id: data.framework_id,
      framework_version_id: data.framework_version_id,
      template_node_id: data.template_node_id,
      mapping_type: data.mapping_type,
      external_clause_ref: data.external_clause_ref !== undefined ? data.external_clause_ref : null,
      notes: data.notes !== undefined ? data.notes : null,
    };

    const response = await fetch(`${API_BASE}/framework-mapping/framework-mappings`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(cleanData),
    });

    const result: ApiResponse<{ id: number }> = await handleResponse(response);

    // Return created mapping by fetching it
    const mappings = await frameworkMapperApi.getNodeMappings(data.template_node_id);
    return mappings.find(m => m.id === result.data.id)!;
  },

  /**
   * Update a framework mapping
   * PUT /api/templates/framework-mappings/:id
   * Note: Only external_clause_ref, mapping_type, and notes can be updated
   */
  async updateMapping(id: number, data: UpdateFrameworkMappingRequest): Promise<FrameworkMappingResponse> {
    // Ensure undefined values are converted to null for SQL compatibility
    const cleanData = {
      external_clause_ref: data.external_clause_ref !== undefined ? data.external_clause_ref : null,
      mapping_type: data.mapping_type,
      notes: data.notes !== undefined ? data.notes : null,
    };

    const response = await fetch(`${API_BASE}/framework-mapping/framework-mappings/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(cleanData),
    });

    await handleResponse(response);
    
    // Return updated mapping (we don't have endpoint to fetch by ID)
    // Caller should refresh list
    return {} as FrameworkMappingResponse;
  },

  /**
   * Delete a framework mapping
   * DELETE /api/templates/framework-mappings/:id
   */
  async deleteMapping(id: number): Promise<void> {
    const response = await fetch(`${API_BASE}/framework-mapping/framework-mappings/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    await handleResponse(response);
  },

  // ==========================================================================
  // CONVENIENCE METHODS
  // ==========================================================================

  /**
   * Get all mappings for a specific framework
   * Note: Backend doesn't support filtering by framework_id directly
   */
  async getMappingsForFramework(frameworkId: number): Promise<FrameworkMappingResponse[]> {
    // Get mappings for each template (inefficient, but backend limitation)
    const templates = await this.getTemplates();
    const allMappings: FrameworkMappingResponse[] = [];

    for (const template of templates) {
      const templateMappings = await this.getTemplateMappings(template.id);
      allMappings.push(...templateMappings);
    }

    // Filter by framework_id
    return allMappings.filter(m =>
      m.framework?.id === frameworkId
    );
  },

  /**
   * Get all mappings for a template (via all its nodes)
   */
  async getMappingsForTemplate(templateId: number): Promise<FrameworkMappingResponse[]> {
    return this.getTemplateMappings(templateId);
  },
};
